import {
  PreviousItemAnswersResponse,
  ReassignItemDataResponse
} from '@/main/models/response';
import { getTranslateByKey } from '@/main/services/utils';
import { convertStringForBoolean } from '@/main/services/utilsString';
import { z } from 'zod';

export enum ReassignModeEnum {
  Single = 'single',
  Bulk = 'bulk',
  Cancelled = 'cancelled'
}

export type ReassignPartData = ReassignItemDataResponse;

export function getAnswerPrice(answer: PreviousItemAnswersResponse): number {
  return answer.currentPrice ?? answer.answeredPrice;
}

const requiredMsg = (fieldKey: string) =>
  getTranslateByKey('information.isRequired', true).replace(
    '{0}',
    getTranslateByKey(fieldKey)
  );

const keyValueSchema = z.object({
  key: z.union([z.string(), z.number()]).nullish(),
  value: z.string(),
  default: z.string().nullish()
});

const supplierItemFormSchema = z.object({
  guidQuoteSupplierItem: z.string(),
  partType: keyValueSchema.nullable(),
  deliveryDeadline: z.string(),
  price: z.string()
});

const directAssignSchema = z.object({
  supplier: keyValueSchema.nullable(),
  partType: keyValueSchema.nullable(),
  deliveryDeadline: z.string().nullable(),
  price: z.string().nullable()
});

const basePartSchema = z.object({
  guidProcurementSupplierItem: z.string(),
  quoteGuid: z.string(),
  supplierItems: z.array(supplierItemFormSchema),
  selectedGuidQuoteSupplierItem: z.string().nullable(),
  directAssign: directAssignSchema.nullable(),
  reason: keyValueSchema.nullable(),
  reasonDescription: z.string().nullable()
});

type BasePart = z.infer<typeof basePartSchema>;

function validatePart(
  mode: ReassignModeEnum,
  data: BasePart,
  ctx: z.RefinementCtx
) {
  const hasSelection = data.selectedGuidQuoteSupplierItem !== null;
  const hasDirectAssign = data.directAssign !== null;
  const hasAnyDecision = hasSelection || hasDirectAssign;

  if (hasSelection) {
    const selectedIdx = data.supplierItems.findIndex(
      item => item.guidQuoteSupplierItem === data.selectedGuidQuoteSupplierItem
    );
    if (selectedIdx !== -1) {
      const selected = data.supplierItems[selectedIdx];
      if (!selected.partType) {
        ctx.addIssue({
          code: 'custom',
          path: ['supplierItems', selectedIdx, 'partType'],
          message: requiredMsg('information.partTypes')
        });
      }
      if (!selected.deliveryDeadline?.trim()) {
        ctx.addIssue({
          code: 'custom',
          path: ['supplierItems', selectedIdx, 'deliveryDeadline'],
          message: requiredMsg('information.deliveryDeadline')
        });
      }
      if (!selected.price?.trim()) {
        ctx.addIssue({
          code: 'custom',
          path: ['supplierItems', selectedIdx, 'price'],
          message: requiredMsg('information.price')
        });
      }
    }
  }

  if (hasAnyDecision && !data.reason) {
    ctx.addIssue({
      code: 'custom',
      path: ['reason'],
      message: requiredMsg('information.reason')
    });
  }
  if (
    hasAnyDecision &&
    data.reason &&
    convertStringForBoolean(data.reason.default) &&
    !data.reasonDescription?.trim()
  ) {
    ctx.addIssue({
      code: 'custom',
      path: ['reasonDescription'],
      message: requiredMsg('information.description')
    });
  }
  if (hasDirectAssign && data.directAssign) {
    const d = data.directAssign;
    if (!d.supplier) {
      ctx.addIssue({
        code: 'custom',
        path: ['directAssign', 'supplier'],
        message: requiredMsg('information.supplier')
      });
    }
    if (!d.partType) {
      ctx.addIssue({
        code: 'custom',
        path: ['directAssign', 'partType'],
        message: requiredMsg('information.partTypes')
      });
    }
    if (!d.deliveryDeadline?.trim()) {
      ctx.addIssue({
        code: 'custom',
        path: ['directAssign', 'deliveryDeadline'],
        message: requiredMsg('information.deliveryDeadline')
      });
    } else if (mode !== ReassignModeEnum.Cancelled && Number(d.deliveryDeadline) < 1) {
      ctx.addIssue({
        code: 'custom',
        path: ['directAssign', 'deliveryDeadline'],
        message: getTranslateByKey(
          'information.notPossibleToChangeDeliveryDate'
        )
      });
    }
    if (!d.price?.trim()) {
      ctx.addIssue({
        code: 'custom',
        path: ['directAssign', 'price'],
        message: requiredMsg('information.price')
      });
    }
  }
}

export function buildReassignFormSchema(mode: ReassignModeEnum) {
  return z.object({
    parts: z.array(
      basePartSchema.superRefine((data, ctx) => validatePart(mode, data, ctx))
    )
  });
}

export function getReassignModeEnum(isBulk: boolean, cancelledPartsMode: boolean): ReassignModeEnum {
  if (cancelledPartsMode) {
    return ReassignModeEnum.Cancelled;
  }
  return isBulk ? ReassignModeEnum.Bulk : ReassignModeEnum.Single;
}

export type ReassignForm = z.infer<ReturnType<typeof buildReassignFormSchema>>;
export type PartFormValue = z.infer<typeof basePartSchema>;
